import "../styles/rare.css";
import "../styles/petMoments.css";
import type { CatchGrant } from "@highmarch/app";
import type { PetData } from "@highmarch/domain";
import { bramStill } from "../guide/bram/bramView";
import { motionOk } from "../kit";
import { petFaceHtml } from "./petArt";
import { catchCardWords, escapeWords, wildBannerWords } from "./petWords";

/**
 * The wild pup's DOM in battle (Pets v2, docs/PETS.md), in the rare spawn's dress (ui/rareUi.ts, rare.css) with the
 * pup's own colours: the banner as it bounds onto the grass ("A WILD MUSTER PUP!", a rare coat named when it wears one),
 * the bobbing TAP! tag over it while it romps, the mid-battle catch card (what the catch paid, a new coat, Bram's cheer;
 * a tap sends it off early) and the escape note. Pointer-transparent but for the card; nothing here pauses the battle.
 */
export const PET_MOMENT = {
  /** The notice holds this long: a heads-up, never a spotlight (the player decides whether to chase). */
  bannerMs: 3200,
  cardMs: 3200,
  escapeMs: 2600,
  /** Gold and the coat's sparkle pink, as the rare's colour pair. */
  a: "#ffd75c", b: "#ff9fc4",
} as const;

function paint(el: HTMLElement): void { el.style.setProperty("--rare-a", PET_MOMENT.a); el.style.setProperty("--rare-b", PET_MOMENT.b); }

/**
 * The notice as the wild pup appears (`skinName`: the rare coat's name it wears, or null): a slim note under the top
 * bar with its face and one line — not a banner over the field, and the camera never moves to it (Kevin, 2026-10-14:
 * pets come often, so the player decides whether to engage). The pup is found on the field by its ring and TAP! tag.
 */
export class WildPetBanner {
  private el: HTMLDivElement | null = null;
  private timer = 0;
  constructor(private readonly parent: HTMLElement) {}

  show(pet: PetData, skin: string | null, skinName: string | null): void {
    this.hide();
    const w = wildBannerWords(pet, skinName);
    const el = document.createElement("div");
    el.className = `wildnote plate${skin ? " coat" : ""}`;
    paint(el);
    el.innerHTML = `<span class="wn-face">${petFaceHtml(pet.id, skin)}</span>`
      + `<span class="wn-text"><b class="disp">${w.title}</b><small>${w.sub}</small></span>`;
    this.parent.append(el);
    this.el = el;
    if (motionOk() && typeof el.animate === "function") el.animate([{ transform: "translate(-50%, -8px)", opacity: 0 }, { transform: "translate(-50%, 0)", opacity: 1 }], { duration: 260, easing: "ease-out", fill: "backwards" });
    this.timer = window.setTimeout(() => this.leave(), PET_MOMENT.bannerMs);
  }

  private leave(): void {
    const el = this.el;
    if (!el) return;
    if (!motionOk() || typeof el.animate !== "function") { this.hide(); return; }
    el.animate([{ opacity: 1 }, { opacity: 0, transform: "translate(-50%, -8px)" }], { duration: 320, easing: "ease-in", fill: "forwards" }).onfinish = () => this.hide();
  }

  hide(): void { window.clearTimeout(this.timer); this.el?.remove(); this.el = null; }
}

/** The bobbing "TAP!" tag over the romping pup (placed every frame at its screen point; hidden when it is not out). */
export class WildPetTag {
  readonly el: HTMLDivElement;
  constructor(parent: HTMLElement) {
    this.el = document.createElement("div");
    this.el.className = "wildtag disp hidden";
    this.el.innerHTML = `<span>TAP!</span><i></i>`;
    parent.append(this.el);
  }
  /** At (x, y) CSS px (the pup's head), or hidden (null). */
  place(at: { x: number; y: number } | null): void {
    this.el.classList.toggle("hidden", at === null);
    if (at) this.el.style.transform = `translate(${Math.round(at.x)}px, ${Math.round(at.y)}px)`;
  }
  dispose(): void { this.el.remove(); }
}

/**
 * The catch: a card over the map — the pup's portrait in its coat, "Muster Pup caught!" (or "Lv 7 → 8", or the gold at
 * the cap), "New coat: Snowdrift!" when it wore a new one, and Bram cheering — then it flies to the pet's HUD chip (or
 * just away). A tap sends it on at once.
 */
export class PetCatchCard {
  private el: HTMLDivElement | null = null;
  private timers: number[] = [];
  constructor(private readonly parent: HTMLElement, private readonly target: () => DOMRect | null) {}

  show(grant: CatchGrant, pet: PetData, skinName: string | null): void {
    this.hide();
    const w = catchCardWords(grant, pet, skinName);
    const el = document.createElement("div");
    el.className = `rare-chest petcatch${w.coat ? " coat" : ""}`;
    paint(el);
    el.innerHTML = `<div class="rc-rays"></div><div class="rc-card"><div class="rc-chest pc-face">${petFaceHtml(pet.id, grant.skin)}<i class="rc-glow"></i></div>`
      + `<b class="rc-label disp">${w.title}</b><small class="rc-sub">${w.sub}</small>`
      + (w.coat ? `<span class="pc-coat disp">${w.coat}</span>` : "")
      + `<div class="rc-bram"><div class="rc-bust"></div><div class="rc-say">${w.bram}</div></div></div>`;
    el.querySelector(".rc-bust")!.append(bramStill("cheering", 64));
    el.addEventListener("click", () => this.fly());
    this.parent.append(el);
    this.el = el;
    if (motionOk() && typeof el.animate === "function") {
      el.querySelector<HTMLElement>(".rc-card")!.animate([{ transform: "translateY(60px) scale(0.4)", opacity: 0 }, { transform: "translateY(-6px) scale(1.06)", opacity: 1, offset: 0.65 }, { transform: "none", opacity: 1 }], { duration: 540, easing: "ease-out", fill: "backwards" });
      el.querySelector<HTMLElement>(".pc-face img")?.animate([{ transform: "rotate(0)" }, { transform: "rotate(-8deg) scale(1.1)", offset: 0.3 }, { transform: "rotate(6deg) scale(1.1)", offset: 0.6 }, { transform: "rotate(0)" }], { duration: 700, delay: 540 });
      el.querySelector<HTMLElement>(".pc-coat")?.animate([{ transform: "scale(1.8) rotate(-6deg)", opacity: 0 }, { transform: "scale(0.95)", opacity: 1, offset: 0.6 }, { transform: "none", opacity: 1 }], { duration: 420, delay: 760, easing: "ease-out", fill: "backwards" });
    }
    this.timers.push(window.setTimeout(() => this.fly(), PET_MOMENT.cardMs));
  }

  private fly(): void {
    const el = this.el;
    if (!el || el.classList.contains("flying")) return;
    el.classList.add("flying");
    const card = el.querySelector<HTMLElement>(".rc-card")!, to = this.target(), from = card.getBoundingClientRect();
    if (!motionOk() || typeof card.animate !== "function" || !to) { this.hide(); return; }
    const dx = to.left + to.width / 2 - (from.left + from.width / 2), dy = to.top + to.height / 2 - (from.top + from.height / 2);
    el.querySelector<HTMLElement>(".rc-rays")?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 240, fill: "forwards" });
    card.animate([{ transform: "none", opacity: 1 }, { transform: `translate(${dx}px, ${dy}px) scale(0.12)`, opacity: 0.2 }], { duration: 560, easing: "cubic-bezier(.5,0,.8,.6)", fill: "forwards" }).onfinish = () => this.hide();
  }

  get shown(): boolean { return this.el !== null; }
  hide(): void { for (const t of this.timers) window.clearTimeout(t); this.timers = []; this.el?.remove(); this.el = null; }
}

/** It reached the gate: "It got away!", and that it'll be back. */
export class PetEscapeNote {
  private el: HTMLDivElement | null = null;
  private timer = 0;
  constructor(private readonly parent: HTMLElement) {}

  show(pet: PetData, skin: string | null): void {
    this.hide();
    const w = escapeWords(pet);
    const el = document.createElement("div");
    el.className = "rare-escape petescape";
    paint(el);
    el.innerHTML = `<div class="re-face">${petFaceHtml(pet.id, skin)}</div><div class="ribbon disp re-title">${w.title}</div><div class="plate re-sub">${w.sub}</div>`;
    this.parent.append(el);
    this.el = el;
    if (motionOk() && typeof el.animate === "function") el.querySelector<HTMLElement>(".re-title")!.animate([{ transform: "scale(1.7)", opacity: 0 }, { transform: "none", opacity: 1 }], { duration: 380, easing: "ease-out", fill: "backwards" });
    this.timer = window.setTimeout(() => this.hide(), PET_MOMENT.escapeMs);
  }

  hide(): void { window.clearTimeout(this.timer); this.el?.remove(); this.el = null; }
}
